/* eslint max-statements: ["error", {max: 34}] -- this applies for the entire file */
/* eslint max-lines: ["error", {max: 300}] -- This modal is complex */
/* eslint max-lines-per-function: ["error", {max: 250}] -- This modal is complex */
/* eslint complexity: ["error", {max: 13}] -- This modal is complex */
import { useCallback, useEffect, useRef, useState } from "react";

import every from "lodash/every";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { Alert } from "react-native";

import { ImageCompressionSource } from "@/constants/analytics/Analytics";
import {
  AcceptedDocumentMimeTypes,
  DocumentMimeType,
  DocumentTag,
} from "@/constants/Documents";
import { TasksTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useTasksFileUpload from "@/hooks/useTasksFileUpload";
import type { MinimalPatientDocument } from "@/hooks/useTasksFileUpload";
import { useToast } from "@/hooks/useToast";
import useAppMonitor from "@/services/AppMonitor";
import { useGetTasksQuery } from "@/store/api/tasks";
import { useAppSelector } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { selectTaskDetailsData } from "@/store/reducers/modals/taskDetailsModal";
import type { CardSide } from "@/ui/components/tasks/InsuranceOverviewCard";
import TaskDetailsModalWrapper, {
  FooterActionLabel,
} from "@/ui/components/tasks/TaskDetailsModalWrapper";
import UploadInsuranceCardTaskDetails from "@/ui/components/tasks/UploadInsuranceCardTaskDetails";
import FilePickerModal from "@/ui/composed/pickers/FilePickerModal";
import { FullScreenSpinner } from "@/ui/core";
import { isWeb } from "@/utils/platform.util";
import { convertAssetToFile, isNotNilNotEmpty } from "@/utils/shared.utils";

const baseTestID = TasksTestID.TaskDetails;

// on native, two buttons are shown: "Choose from Library" (images), "Upload a file" (PDFs)
// on web, there is no "Library", so one "Upload a file" button is shown that supports both PDfs + images
const InsuranceDocumentMimeTypes = isWeb()
  ? AcceptedDocumentMimeTypes.InsuranceUpload
  : [DocumentMimeType.PDF];

const VoidErrorText =
  "We’re sorry, we were unable to process your request at this time. Please try again.";

const InsuranceCardTag = DocumentTag.Insurance;

const getSuffix = (asset: FilePickerAsset) =>
  asset.fileName.substring(asset.fileName.lastIndexOf("."));

const Modal = () => {
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector() || [];
  const { captureException } = useAppMonitor();
  const { showToast } = useToast();
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [insuranceFrontImageUri, setInsuranceFrontImageUri] =
    useState<FilePickerAsset | null>(null);
  const [insuranceBackImageUri, setInsuranceBackImageUri] =
    useState<FilePickerAsset | null>(null);
  const [isImagePickerModalOpen, setIsImagePickerModalOpen] =
    useState<boolean>(false);
  const [activeImageSide, setActiveImageSide] = useState<CardSide>("front");
  const [documents, setDocuments] = useState<MinimalPatientDocument[]>([]);
  const { refetch: refetchTasks } = useGetTasksQuery({
    appUserId,
    isPatient,
    linkedPatients: proxyPatients,
  });
  const renderCountRef = useRef<number>(1);
  useEffect(() => {
    renderCountRef.current = renderCountRef.current + 1;
  });

  const taskDetailsData = useAppSelector(selectTaskDetailsData);

  const {
    description,
    duration,
    linkedNoteId,
    // selectedUserId could be a support user, while the task itself is for a specific patient
    patientExternalId,
    patientName,
    selectedUserId,
    taskId,
    title,
  } = taskDetailsData || {};

  const { isLoading, uploadFilesV2, voidPatientDocuments } =
    useTasksFileUpload();

  const voidDocumentsAndDismiss = useCallback(async () => {
    try {
      if (!isEmpty(documents)) {
        await voidPatientDocuments({ documents });
      }
    } catch {
      showToast({
        text: VoidErrorText,
        variant: "error",
      });
    } finally {
      router.dismiss();
    }
  }, [documents, showToast, voidPatientDocuments]);

  if (
    isNil(title) ||
    isNil(linkedNoteId) ||
    !isNotNilNotEmpty(selectedUserId) ||
    !isNotNilNotEmpty(taskId)
  ) {
    captureException(
      new Error(
        `upload-insurance modal is missing required params on render #${renderCountRef.current}`,
      ),
    );
    if (isWeb()) {
      router.dismiss();
    }
    return <FullScreenSpinner />;
  }

  const insuranceCardFront = insuranceFrontImageUri?.uri ?? "";
  const insuranceCardBack = insuranceBackImageUri?.uri ?? "";

  const isRightButtonDisabled = !every([insuranceCardFront, insuranceCardBack]);
  const isInsuranceCardEmpty = every(
    [insuranceCardFront, insuranceCardBack],
    (item) => !item,
  );

  const handleAssetSelected = (asset: FilePickerAsset) => {
    if (activeImageSide === "front") {
      setInsuranceFrontImageUri(asset);
    } else {
      setInsuranceBackImageUri(asset);
    }
  };

  const toggleAlert = () => {
    setIsAlertOpen(!isAlertOpen);
  };

  const handleDismiss = async () => {
    if (!isInsuranceCardEmpty) {
      toggleAlert();
      return;
    }
    await voidDocumentsAndDismiss();
  };

  const handleSaveCard = async () => {
    if (isEmpty(insuranceFrontImageUri) || isEmpty(insuranceBackImageUri)) {
      router.dismiss();
      return;
    }

    try {
      // UploadUrlV2 endpoint requires filename to include file extension
      const frontSuffix = getSuffix(insuranceFrontImageUri);
      const backSuffix = getSuffix(insuranceBackImageUri);
      const frontFile: File = await convertAssetToFile({
        file: insuranceFrontImageUri,
        fileName: `Insurance Card Front${frontSuffix}`,
      });
      const backFile: File = await convertAssetToFile({
        file: insuranceBackImageUri,
        fileName: `Insurance Card Back${backSuffix}`,
      });

      if (!isNotNilNotEmpty(patientExternalId)) {
        captureException(
          new Error("upload-insurance v2 modal is missing patientExternalId"),
        );
        return;
      }
      await uploadFilesV2({
        backParams: {
          documentTags: [InsuranceCardTag],
          file: backFile,
          fileName: backFile.name,
          fileSize: backFile.size.toString(),
          patientExternalId,
        },
        frontParams: {
          documentTags: [InsuranceCardTag],
          file: frontFile,
          fileName: frontFile.name,
          fileSize: frontFile.size.toString(),
          patientExternalId,
        },
        selectedUserId,
        setDocuments,
        taskCacheKey: {
          linkedPatients: proxyPatients,
        },
        taskId,
      });

      void refetchTasks();
      router.dismiss();

      showToast({
        text: "Insurance card uploaded",
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      Alert.alert(
        "Error",
        "Failed to upload insurance card, please try again.",
      );
    }
  };

  const handleAlertConfirm = () => {
    void voidDocumentsAndDismiss();
  };

  const handleCardPress = (side: "back" | "front") => {
    setActiveImageSide(side);
    setIsImagePickerModalOpen(true);
  };

  return (
    <TaskDetailsModalWrapper
      footerRightButtonLabel={FooterActionLabel.SaveCard}
      isLoading={isLoading}
      isRightButtonDisabled={isRightButtonDisabled}
      onFooterLeftButtonPress={handleDismiss}
      onFooterRightButtonPress={handleSaveCard}
    >
      <UploadInsuranceCardTaskDetails
        description={description}
        duration={duration || ""}
        insuranceCardBack={insuranceCardBack}
        insuranceCardFront={insuranceCardFront}
        isAlertOpen={isAlertOpen}
        onAlertConfirm={handleAlertConfirm}
        onPressCard={handleCardPress}
        onToggleAlert={toggleAlert}
        patientName={patientName}
        title={title}
      />
      <FilePickerModal
        documentMimeTypes={InsuranceDocumentMimeTypes}
        imageCompressionSource={ImageCompressionSource.Insurance}
        isOpen={isImagePickerModalOpen}
        onAssetSelected={handleAssetSelected}
        onDismiss={() => setIsImagePickerModalOpen(false)}
        testID={baseTestID}
      />
    </TaskDetailsModalWrapper>
  );
};

export default Modal;
